import { lazy, Suspense } from "react";
import { Clock, Flame, ChevronLeft, Heart, Share2, Copy, Check, Utensils } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RecipeRating = lazy(() => import("@/components/RecipeRating"));

export const RecipeDetailModal = ({
  selectedRecipe,
  setSelectedRecipe,
  copiedRecipeId,
  copyRecipeToClipboard,
  shareRecipe,
  toggleSaveRecipe,
  isRecipeSaved,
  openRecipeFeedback,
}) => {
  return (
    <Dialog open={!!selectedRecipe} onOpenChange={() => setSelectedRecipe(null)}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto bg-[#FEFAE0] border-0 p-0 rounded-3xl" aria-describedby={undefined}>
        {selectedRecipe && (
          <div data-testid="recipe-detail-modal">
            <div className="relative">
              {selectedRecipe.image_url ? (
                <img src={selectedRecipe.image_url} alt={selectedRecipe.title} className="w-full h-56 object-cover" />
              ) : (
                <div className="w-full h-56 bg-gradient-to-br from-[#FFB703]/20 to-[#2A9D8F]/20 flex items-center justify-center">
                  <Utensils className="w-16 h-16 text-[#264653]/30" />
                </div>
              )}
              <button
                onClick={() => setSelectedRecipe(null)}
                className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center border-2 border-[#264653]/10 hover:bg-white btn-shadow"
                data-testid="recipe-modal-back-btn"
              >
                <ChevronLeft className="w-5 h-5 text-[#264653]" />
              </button>
              <div className="absolute bottom-4 right-4 flex gap-2">
                <button
                  onClick={(e) => copyRecipeToClipboard(selectedRecipe, e)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all btn-shadow ${
                    copiedRecipeId === selectedRecipe.id ? "bg-[#2A9D8F] text-white" : "bg-white text-[#264653] hover:bg-[#2A9D8F] hover:text-white"
                  }`}
                  data-testid="copy-recipe-modal-btn"
                >
                  {copiedRecipeId === selectedRecipe.id ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
                </button>
                <button
                  onClick={(e) => shareRecipe(selectedRecipe, e)}
                  className="w-12 h-12 rounded-full bg-white text-[#264653] flex items-center justify-center btn-shadow hover:bg-[#FFB703]"
                  data-testid="share-recipe-modal-btn"
                >
                  <Share2 className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => toggleSaveRecipe(selectedRecipe, e)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all btn-shadow ${
                    isRecipeSaved(selectedRecipe.id) ? "bg-[#E76F51] text-white" : "bg-white text-[#264653] hover:bg-[#E76F51] hover:text-white"
                  }`}
                  data-testid="save-recipe-modal-btn"
                >
                  <Heart className={`w-6 h-6 ${isRecipeSaved(selectedRecipe.id) ? "fill-white" : ""}`} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-2xl font-bold text-[#264653] text-left flex-1">
                    {selectedRecipe.title}
                  </DialogTitle>
                  {selectedRecipe.cuisine && (
                    <span className="text-xs px-2 py-1 rounded-full bg-[#264653]/10 text-[#264653]">
                      {selectedRecipe.cuisine}
                    </span>
                  )}
                </div>
              </DialogHeader>

              <div className="flex items-center gap-3 mt-3 mb-4">
                <span className="time-badge"><Clock className="w-4 h-4" />{selectedRecipe.time_minutes} min</span>
                <span className="calorie-badge"><Flame className="w-4 h-4" />{selectedRecipe.calories} cal</span>
              </div>

              {selectedRecipe.nutrition && (
                <div className="bg-gradient-to-r from-[#2A9D8F]/10 to-[#FFB703]/10 rounded-2xl p-4 mb-6">
                  <h4 className="font-bold text-[#264653] mb-3 text-sm">Nutrition per Serving</h4>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[
                      { val: selectedRecipe.nutrition.protein, label: "Protein", color: "#E76F51" },
                      { val: selectedRecipe.nutrition.carbs, label: "Carbs", color: "#FFB703" },
                      { val: selectedRecipe.nutrition.fat, label: "Fat", color: "#2A9D8F" },
                      { val: selectedRecipe.nutrition.fiber, label: "Fiber", color: "#90BE6D" },
                      { val: selectedRecipe.nutrition.sugar, label: "Sugar", color: "#E9C46A" },
                    ].map(({ val, label, color }) => (
                      <div key={label} className="bg-white rounded-xl p-2">
                        <div className="text-lg font-bold" style={{ color }}>{val}g</div>
                        <div className="text-xs text-[#264653]/60">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h4 className="font-bold text-[#264653] mb-3">Ingredients</h4>
                <ul className="space-y-2">
                  {selectedRecipe.ingredients.map((ing, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#264653]/80">
                      <span className="w-2 h-2 rounded-full bg-[#2A9D8F]" />
                      <span>{ing.amount} {ing.name}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-[#264653] mb-3">Steps</h4>
                <ol className="space-y-4">
                  {selectedRecipe.steps.map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="step-number">{i + 1}</span>
                      <span className="text-[#264653]/80 flex-1 pt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bg-gradient-to-r from-[#E76F51]/10 to-[#FFB703]/10 rounded-2xl p-4">
                <h4 className="font-bold text-[#264653] mb-3 text-center">Rate this recipe</h4>
                <Suspense fallback={<div className="h-12" />}>
                  <div className="flex justify-center">
                    <RecipeRating recipe={selectedRecipe} compact={false} onFeedback={openRecipeFeedback} />
                  </div>
                </Suspense>
              </div>

              <div className="bg-[#264653]/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#264653]/60">Recipe Text</span>
                  <button
                    onClick={(e) => copyRecipeToClipboard(selectedRecipe, e)}
                    className={`text-sm font-medium px-3 py-1 rounded-full transition-all ${
                      copiedRecipeId === selectedRecipe.id ? "bg-[#2A9D8F] text-white" : "bg-[#FFB703] text-[#264653] hover:bg-[#FFB703]/80"
                    }`}
                  >
                    {copiedRecipeId === selectedRecipe.id ? "Copied!" : "Copy All"}
                  </button>
                </div>
                <textarea
                  readOnly
                  className="w-full h-32 text-xs text-[#264653]/70 bg-transparent resize-none border-0 focus:outline-none"
                  value={`\u{1F37D}\u{FE0F} ${selectedRecipe.title}\n\u{23F1}\u{FE0F} ${selectedRecipe.time_minutes} min | \u{1F525} ${selectedRecipe.calories} cal\n\n\u{1F4DD} Ingredients:\n${selectedRecipe.ingredients.map(i => `\u2022 ${i.amount} ${i.name}`).join('\n')}\n\n\u{1F468}\u200D\u{1F373} Steps:\n${selectedRecipe.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\nMade with MoodFoodz`}
                  onFocus={(e) => e.target.select()}
                  data-testid="recipe-text-area"
                />
                <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-[#264653]/10">
                  <span className="text-sm text-[#264653]/60">Made with</span>
                  <span className="font-bold text-[#264653]">MoodFoodz</span>
                  <img src="/icons/moodfoodz-icon.png" alt="MoodFoodz" className="w-8 h-8 rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
